import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {of} from 'rxjs';
import {catchError, map, mergeMap, tap, withLatestFrom} from 'rxjs/operators';
import {Bundle} from '../../../models/clare';
import {BundleApiService} from '../../../services/bundle/bundle-api.service';
import {selectSelectedOrganizationId} from '../../../state/router/router.selectors';
import {emptyPagedListResponse, isActiveListSearch, isNotFoundError} from '../../../utils/list-page-utils';
import {buildListApiQueryParams} from '../../product-list/store/product-list-query-params';
import {BundleListActions} from './bundle-list.actions';
import {BundleListService} from './bundle-list.service';

@Injectable()
export class BundleListEffects {
  toggleBundleActiveStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BundleListActions.toggleBundleActiveStatus),
      withLatestFrom(this.store.select(selectSelectedOrganizationId)),
      mergeMap(([{productId, active}, organizationId]) =>
        this.bundleApi.updateBundle(Number(productId), {active}, organizationId).pipe(
          map(bundle => BundleListActions.toggleBundleActiveStatusSuccess({bundle})),
          catchError(() =>
            of(
              BundleListActions.toggleBundleActiveStatusFailure({
                error: {message: 'An error occurred. Please refresh and try again.'},
              })
            )
          )
        )
      )
    )
  );

  loadBundles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BundleListActions.loadBundles),
      withLatestFrom(this.store.select(selectSelectedOrganizationId)),
      mergeMap(([{queryParams}, organizationId]) => {
        const cleanedParams = buildListApiQueryParams(queryParams);
        const catalogId = cleanedParams.catalogId;

        return this.bundleListService.loadBundles(catalogId, cleanedParams, organizationId).pipe(
          map(bundles => BundleListActions.loadBundlesSuccess({bundles})),
          catchError(error => {
            if (isActiveListSearch(cleanedParams) && isNotFoundError(error)) {
              return of(
                BundleListActions.loadBundlesSuccess({
                  bundles: emptyPagedListResponse<Bundle>(cleanedParams, 'Items::Bundle'),
                })
              );
            }

            return of(
              BundleListActions.loadBundlesFailure({
                error: {message: 'An error occurred, please refresh and try again.', code: 'PM-404'},
              })
            );
          })
        );
      })
    )
  );

  loadBundlesFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BundleListActions.loadBundlesFailure),
        tap(({error}) => {
          this.router.navigateByUrl('/error', {state: {error}});
        })
      ),
    {dispatch: false}
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private router: Router,
    private bundleListService: BundleListService,
    private bundleApi: BundleApiService
  ) {}
}
